import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sparkles } from "lucide-react";

/**
 * A warm, hearth-toned SVG ring showing days remaining in the 30-day trial.
 * Uses the existing hearth palette (clay + terracotta + amber + sage) and a
 * subtle inner + outer glow on the leading edge of the arc so the ring feels
 * lit from within — not a generic dashboard donut.
 *
 * Hidden for lifetime_free + active-subscription users. Renders a "Trial
 * ended" red variant post-expiry.
 */
const RADIUS = 44;                // circle radius in SVG units
const STROKE = 8;                 // stroke width
const CIRC = 2 * Math.PI * RADIUS;
const TRIAL_TOTAL = 30;

function ringColour(days, expired) {
  if (expired) return { arc: "#c94a3a", glow: "#c94a3a" };            // deep terracotta / red
  if (days <= 3) return { arc: "#c66b2a", glow: "#e08a3d" };          // clayDark → clay
  if (days <= 10) return { arc: "#d99860", glow: "#e6b98a" };         // amber
  return { arc: "#8ba888", glow: "#b6cdb2" };                          // sage
}

export default function TrialRingCard() {
  const { billing } = useAuth();
  if (!billing) return null;
  if (billing.status === "lifetime_free" || billing.status === "active") return null;

  const expired = billing.status === "trial_expired";
  const days = expired ? 0 : Math.max(0, billing.trial_days_remaining ?? 0);
  const used = TRIAL_TOTAL - days;
  const pct = Math.min(1, Math.max(0, used / TRIAL_TOTAL));
  const dash = CIRC * pct;             // consumed portion
  const remainingDash = CIRC - dash;   // shown as bright colour

  const { arc, glow } = ringColour(days, expired);
  const glowId = "hh-trial-ring-glow";

  return (
    <div
      data-testid="trial-ring-card"
      className="relative rounded-3xl border border-hearth-line/70 bg-gradient-to-br from-hearth-cream via-white to-hearth-stone/40 p-6 flex items-center gap-6 shadow-sm overflow-hidden"
    >
      {/* soft grain / warmth overlay */}
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(217,152,96,0.12),transparent_60%)]" />

      <div className="relative shrink-0" style={{ width: 128, height: 128 }}>
        <svg
          viewBox="0 0 100 100"
          width="128"
          height="128"
          className="drop-shadow-sm"
          aria-hidden
        >
          <defs>
            <filter id={glowId} x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur stdDeviation="1.8" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          {/* base track */}
          <circle
            cx="50"
            cy="50"
            r={RADIUS}
            fill="none"
            stroke="rgba(90, 74, 63, 0.12)"    /* hearth-ink at 12% */
            strokeWidth={STROKE}
          />
          {/* progress arc — remaining days (bright) */}
          <circle
            cx="50"
            cy="50"
            r={RADIUS}
            fill="none"
            stroke={arc}
            strokeOpacity="0.35"
            strokeWidth={STROKE}
            strokeLinecap="round"
            strokeDasharray={CIRC}
            strokeDashoffset={dash}
            transform="rotate(-90 50 50)"
          />
          {/* highlighted leading edge — a short bright glow segment on top */}
          <circle
            cx="50"
            cy="50"
            r={RADIUS}
            fill="none"
            stroke={glow}
            strokeWidth={STROKE + 0.5}
            strokeLinecap="round"
            strokeDasharray={`${Math.max(2, remainingDash * 0.15)} ${CIRC}`}
            strokeDashoffset={dash}
            transform="rotate(-90 50 50)"
            filter={`url(#${glowId})`}
          />
        </svg>
        {/* centre label */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <span
            data-testid="trial-ring-days"
            className="font-display text-4xl font-semibold tracking-tight text-hearth-ink leading-none"
          >
            {expired ? "0" : days}
          </span>
          <span className="mt-1 text-[10px] tracking-widest uppercase text-hearth-mute">
            {days === 1 ? "day" : "days"}
          </span>
        </div>
      </div>

      <div className="relative min-w-0">
        <p className="caption text-hearth-clayDark tracking-widest">
          {expired ? "Trial ended" : "Free trial"}
        </p>
        <h3 className="font-display text-2xl font-semibold tracking-tight mt-1">
          {expired
            ? "Your month with us is up."
            : days === 0
              ? "Last day of your trial."
              : days === 1
                ? "One day left in your trial."
                : `${days} days left in your trial.`}
        </h3>
        <p className="text-sm text-hearth-mute mt-1 max-w-md">
          {expired
            ? "Pick a plan below to unlock adding, editing and the AI scanners again."
            : "Everything you've added stays. When the trial ends the house switches to read-only until you subscribe."}
        </p>
        <Link
          to="/upgrade"
          data-testid="trial-ring-cta"
          className="inline-flex items-center gap-2 mt-4 rounded-full bg-hearth-terracotta hover:bg-hearth-terracottaDark text-white px-5 py-2.5 text-sm font-medium transition"
        >
          <Sparkles size={14} />
          {expired ? "Choose a plan" : "See plans"}
        </Link>
      </div>
    </div>
  );
}
